/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useEffect, useState } from 'react';

import { QueryWrapper } from '@/components/QueryWrapper';
import { useMessage } from '@/hooks/message';
import { useJobNotificationsQuery } from '@/hooks/query/useJobNotificationsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { PauseBannersModal } from '@/pages/Jobs/PauseBannersModal';
import { getJobBannersPausedState } from '@/state/jobBannersSlice';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { JobBanner } from './JobBanner';

const ONE_MINUTE_MS = 60 * 1000;

export function JobBanners() {
  const { t } = useMimicTranslation('jobs');
  const [message] = useMessage();
  const jobBannersPaused = useMgmtPlaneStateStore((state) => state.jobBannersPaused);
  const { setJobBannersPaused } = useMgmtPlaneStateStore.getState();
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    if (!jobBannersPaused) return undefined;

    const interval = setInterval(() => {
      if (!getJobBannersPausedState()) {
        setJobBannersPaused(false);
      }
    }, ONE_MINUTE_MS);

    return () => clearInterval(interval);
  }, [jobBannersPaused, setJobBannersPaused]);

  const jobNotificationsQuery = useJobNotificationsQuery();

  const handleConfirm = () => {
    setJobBannersPaused(true);
    setModalOpen(false);
    message.success(t('banner.bannersPaused'));
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  const handleModalOpen = () => {
    setModalOpen(true);
  };

  if (jobNotificationsQuery.isLoading || jobBannersPaused) return null;

  return (
    <div className="job-banners" data-testid="job-banners">
      <QueryWrapper queryResult={jobNotificationsQuery} errorToast={t('banner.jobStatusError')}>
        {(jobsData) => {
          const { totalErrors, totalWarnings, totalRunning, totalTimeouts } = jobsData.data;
          return (
            <>
              {totalErrors > 0 && (
                <JobBanner notificationType="error" notificationCount={totalErrors} handleModalOpen={handleModalOpen} />
              )}
              {totalTimeouts > 0 && (
                <JobBanner
                  notificationType="timeout"
                  notificationCount={totalTimeouts}
                  handleModalOpen={handleModalOpen}
                />
              )}
              {totalWarnings > 0 && (
                <JobBanner
                  notificationType="warning"
                  notificationCount={totalWarnings}
                  handleModalOpen={handleModalOpen}
                />
              )}
              {totalRunning > 0 && (
                <JobBanner
                  notificationType="running"
                  notificationCount={totalRunning}
                  handleModalOpen={handleModalOpen}
                />
              )}
            </>
          );
        }}
      </QueryWrapper>
      <PauseBannersModal open={modalOpen} onConfirm={handleConfirm} onCancel={handleCancel} />
    </div>
  );
}
